import React, { useState } from 'react';
import { importSuppliers } from '../../../../models/suppliersModel';

const ImportSuppliersModal = ({ onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);

  const handleFileChange = (selectedFile) => {
    if (selectedFile) {
      // Validate file type
      const allowedTypes = ['.csv', 'text/csv', 'application/vnd.ms-excel'];
      const fileExtension = selectedFile.name.toLowerCase().split('.').pop();
      const fileType = selectedFile.type;

      if (fileExtension !== 'csv' && !allowedTypes.includes(fileType)) {
        setError('Please select a CSV file.');
        return;
      }

      setFile(selectedFile);
      setError('');
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    handleFileChange(selectedFile);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange(e.dataTransfer.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      setError('Please select a file to import.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await importSuppliers(file);
      if (response.status === 'success') {
        onSuccess();
      } else {
        setError(response.message || 'Failed to import suppliers');
      }
    } catch (error) {
      console.error('Error importing suppliers:', error);
      setError('Failed to import suppliers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  const downloadSample = () => {
    // Create a sample CSV content
    const sampleData = [
      ['VendorID', 'Company Name', 'Group Code', 'Company Reg', 'Contact Person', 'Email', 'Mobile Phone', 'BBBEE Type', 'BBBEE Level', 'BBBEE Expiry', 'COIDA Number', 'COIDA Expiry', 'Tax Number', 'Tax Expiry', 'Status'],
      ['V001', 'Sample Company Ltd', 'GRP001', '2023/123456/07', 'John Doe', 'john@samplecompany.com', '+27 82 123 4567', 'Generic', 'Level 4', '2024-12-31', 'COIDA123456', '2024-12-31', 'TAX123456', '2024-12-31', '1'],
      ['V002', 'Another Supplier', 'GRP002', '2022/654321/07', 'Jane Smith', 'jane@anothersupplier.com', '+27 83 987 6543', 'EME', 'Level 2', '2025-06-30', 'COIDA654321', '2025-06-30', 'TAX654321', '2025-06-30', '1']
    ];

    // Convert to CSV string
    const csvContent = sampleData.map(row => 
      row.map(field => `"${field}"`).join(',')
    ).join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'suppliers_sample.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div 
      className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Import Suppliers</h3>
            <button
              type="button"
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="text-sm text-blue-700">
                <p className="font-medium mb-2">Import Instructions:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Upload a CSV file with supplier data</li>
                  <li>The first row should contain column headers</li>
                  <li>Required columns: VendorID, Company Name, Group Code, Contact Person, Email</li>
                  <li>Status should be 1 for Active, 0 for Inactive</li>
                  <li>Dates should be in YYYY-MM-DD format</li>
                </ul>
              </div>
            </div>

            {/* Sample download */}
            <div className="text-center">
              <button
                type="button"
                onClick={downloadSample}
                className="text-sm text-indigo-600 hover:text-indigo-800 underline"
              >
                Download Sample CSV File
              </button>
            </div>

            {/* Error message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="text-sm text-red-700">{error}</div>
              </div>
            )}

            {/* File upload area */}
            <div
              className={`relative border-2 border-dashed rounded-lg p-6 text-center ${
                dragActive 
                  ? 'border-indigo-500 bg-indigo-50' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                type="file"
                accept=".csv,text/csv,application/vnd.ms-excel"
                onChange={handleFileSelect}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                disabled={loading}
              />
              
              <div className="space-y-2">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                </svg>
                {file ? (
                  <div>
                    <p className="text-sm text-gray-600">Selected file:</p>
                    <p className="text-sm font-medium text-gray-900">{file.name}</p>
                    <p className="text-xs text-gray-500">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-gray-600">
                      Drop your CSV file here, or{' '}
                      <span className="text-indigo-600 hover:text-indigo-800 underline">
                        click to browse
                      </span>
                    </p>
                    <p className="text-xs text-gray-500">CSV files up to 10MB</p>
                  </div>
                )}
              </div>
            </div>

            {/* File info */}
            {file && (
              <div className="bg-green-50 border border-green-200 rounded-md p-4">
                <div className="text-sm text-green-700">
                  <p className="font-medium">File ready for import</p>
                  <p>Click "Import Suppliers" to proceed with the upload.</p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!file || loading}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Importing...
                </div>
              ) : (
                'Import Suppliers'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ImportSuppliersModal;
